import type { Order, OrderItem, OrderStatus, PaymentItemAllocation } from '../types';
import { isUnpaidOnlineWebOrder } from './onlineOrderVisibility';
import { orderCreatedAtMs, toMillis, ymdInTimeZone } from './orderTimestamps';

/** Use only quantityCompleted for per-card completion logic. Never infer from status. */
export function getEffectiveQuantityCompleted(item: Order['items'][0]): number {
  return item.quantityCompleted ?? 0;
}

/** Per-card served: when quantityServedByBatch exists, use it so serving one card doesn't mark the other. */
export function getEffectiveQuantityServedForBatch(
  item: Order['items'][0],
  batchIndex: number | undefined,
  _targetQty: number
): number {
  // When we have per-batch served data, always use it for that specific card.
  if (batchIndex != null && item.quantityServedByBatch && typeof item.quantityServedByBatch === 'object') {
    const perBatch = item.quantityServedByBatch[String(batchIndex)];
    if (typeof perBatch === 'number') return perBatch;
  }

  // For running-order cards (batchIndex >= 1), never fall back to global served,
  // otherwise previously served quantities from earlier batches can make a fresh
  // running card appear fully served as soon as it's created.
  if (batchIndex != null && batchIndex >= 1) {
    return 0;
  }

  // For the base card (no batchIndex / legacy orders), fall back to the legacy
  // per-item served fields so older data without quantityServedByBatch still works.
  return item.quantityServed ?? (item.served ? item.quantity : 0);
}

/** Per-card completion: when quantityCompletedByBatch exists, use it so running-first doesn't mark main done. */
export function getEffectiveQuantityCompletedForBatch(
  item: Order['items'][0],
  batchIndex: number | undefined,
  _targetQty: number
): number {
  if (batchIndex != null && item.quantityCompletedByBatch && typeof item.quantityCompletedByBatch === 'object') {
    const perBatch = item.quantityCompletedByBatch[String(batchIndex)];
    if (typeof perBatch === 'number') return perBatch;
    if (batchIndex >= 1) return 0;
  }
  if (batchIndex != null && batchIndex >= 1) return 0;
  return item.quantityCompleted ?? 0;
}

const isDiscountItem = (item: Order['items'][0]) =>
  (typeof item.price === 'number' && item.price < 0) ||
  String(item.id).includes('combo-discount');

/**
 * Compute the First Table discount for an order based on currently stored order fields.
 * Assumes firstTableDiscountAmount has been computed and stored on the order by POS logic.
 */
export function getFirstTableDiscount(order: Order): number {
  if (order.discountMethod !== 'first-table') return 0;
  return Math.max(0, order.firstTableDiscountAmount ?? 0);
}

/**
 * Get items belonging to a card (base or running batch) and their completion targets.
 * Returns { items: [{ item, originalIndex, targetQty }], allCompleted, allServed }
 */
export function getCardItemsWithTargets(
  order: Order,
  batchIndex: number | undefined
): {
  items: { item: Order['items'][0]; originalIndex: number; targetQty: number; baselineQty?: number }[];
  allCompleted: boolean;
  allServed: boolean;
} {
  const items = order.items ?? [];
  const batches = order.orderBatches ?? [];
  // For KDS/service base quantities, prefer the frozen initialKitchenSnapshot;
  // fall back to lastSentItemSnapshot for older orders.
  const snapshot = order.initialKitchenSnapshot ?? order.lastSentItemSnapshot ?? [];
  const snapshotByLineId = new Map(snapshot.map((s) => [s.lineId, s]));
  const initialQty = new Map<string, number>();
  items.forEach((item, idx) => {
    if (item.removed) return;
    const lineId = item.lineId ?? `idx-${idx}`;
    const prev = item.lineId ? snapshotByLineId.get(item.lineId) : snapshot[idx];
    if (prev) initialQty.set(lineId, prev.quantity);
  });

  let cardItems: { item: Order['items'][0]; originalIndex: number; targetQty: number }[] = [];
  if (batchIndex == null) {
    const initialBatch = batches[0];
    const allLineIndices = items.map((_, i) => i);
    const activeKitchenIndices = allLineIndices.filter((idx) => {
      const item = items[idx];
      return !!item && !isDiscountItem(item) && !item.removed;
    });
    let indices: number[];
    if (initialBatch?.itemIndices?.length) {
      const fromBatch = initialBatch.itemIndices.filter((idx) => idx >= 0 && idx < items.length);
      const batchSet = new Set(fromBatch);
      /** POS can append lines after batch 0 was written; stale itemIndices would hide them on KDS. */
      const batchMissesActiveLine = activeKitchenIndices.some((idx) => !batchSet.has(idx));
      const hasRunningKitchenBatch = batches.length > 1;

      if (!batchMissesActiveLine) {
        indices = fromBatch;
      } else if (!hasRunningKitchenBatch) {
        // Single-card order: keep showing every active line if batch 0 metadata is stale.
        indices = allLineIndices;
      } else {
        /**
         * Running batch exists: never expand main card to `allLineIndices` — that duplicates
         * new lines on both base and running tickets. Only merge indices for lines that were
         * on the initial kitchen snapshot (same lineId or legacy index row).
         */
        const merged = new Set(fromBatch);
        for (const idx of activeKitchenIndices) {
          if (merged.has(idx)) continue;
          const item = items[idx];
          if (!item || isDiscountItem(item) || item.removed) continue;
          const prev = item.lineId ? snapshotByLineId.get(item.lineId) : snapshot[idx];
          if (prev) merged.add(idx);
        }
        indices = Array.from(merged).sort((a, b) => a - b);
      }
    } else {
      indices = allLineIndices;
    }
    cardItems = indices
      .map((idx) => {
        const item = items[idx];
        if (!item || isDiscountItem(item) || item.removed) return null;
        const lineId = item.lineId ?? `idx-${idx}`;
        const targetQty = initialQty.get(lineId) ?? item.quantity;
        return { item, originalIndex: idx, targetQty };
      })
      .filter((x): x is NonNullable<typeof x> => x != null);
  } else if (batchIndex >= 1 && batches[batchIndex]) {
    const batch = batches[batchIndex];
    const deltaMap = new Map<number, number>();
    (batch.deltas ?? []).forEach((d) => {
      if (typeof d.index === 'number' && typeof d.deltaQty === 'number' && d.deltaQty > 0) {
        deltaMap.set(d.index, d.deltaQty);
      }
    });
    const hasExplicitDeltas = deltaMap.size > 0;
    const getBaseline = (idx: number, lineId: string) => {
      let base = initialQty.get(lineId) ?? 0;
      for (let b = 1; b < batchIndex; b++) {
        const d = batches[b]?.deltas?.find((x) => x.index === idx);
        if (d && d.deltaQty > 0) base += d.deltaQty;
      }
      return base;
    };
    const rowForIndex = (idx: number) => {
      const item = items[idx];
      if (!item || isDiscountItem(item) || item.removed) return null;
      const lineId = item.lineId ?? `idx-${idx}`;
      const baseline = getBaseline(idx, lineId);
      /**
       * Running-order cards must only show true batch deltas.
       * When deltas are present, do not infer from current quantity-baseline,
       * otherwise unchanged base lines can reappear on running cards.
       */
      const deltaQty = hasExplicitDeltas
        ? (deltaMap.get(idx) ?? 0)
        : Math.max(0, item.quantity - baseline);
      const targetQty = deltaQty;
      if (targetQty <= 0) return null;
      return { item, originalIndex: idx, targetQty, baselineQty: baseline };
    };
    const fromBatchIndices = batch.itemIndices.filter((idx) => idx >= 0 && idx < items.length);
    const sourceIndices = hasExplicitDeltas
      ? Array.from(new Set([...fromBatchIndices, ...Array.from(deltaMap.keys())]))
      : fromBatchIndices;
    cardItems = sourceIndices
      .map(rowForIndex)
      .filter((x): x is NonNullable<typeof x> => x != null)
      .sort((a, b) => a.originalIndex - b.originalIndex);
  }

  if (cardItems.length === 0) {
    return { items: [], allCompleted: true, allServed: true };
  }

  const cardBatchIndex = batchIndex != null ? batchIndex : 0;

  const allCompleted = cardItems.every(
    ({ item, targetQty }) =>
      getEffectiveQuantityCompletedForBatch(item, cardBatchIndex, targetQty) >= targetQty
  );

  // For per-card "all served", use per-batch served quantities when available so that
  // a newly created running-order card is not treated as fully served just because the
  // base card (or a previous batch) was already served.
  const allServed = cardItems.every(
    ({ item, targetQty }) =>
      getEffectiveQuantityServedForBatch(item, cardBatchIndex, targetQty) >= targetQty
  );

  return { items: cardItems, allCompleted, allServed };
}

export function getCardCompletionStatus(
  order: Order,
  batchIndex: number | undefined
): { allItemsCompleted: boolean; allItemsServed: boolean } {
  const { allCompleted, allServed } = getCardItemsWithTargets(order, batchIndex);
  return { allItemsCompleted: allCompleted, allItemsServed: allServed };
}

/**
 * Compute order total from current items (exclude removed) and discounts.
 * Matches PrintableReceipt so app display and receipt never disagree.
 */
export function getOrderTotalFromItems(order: Order): number {
  const items = order.items ?? [];
  const totalBeforeDiscount = items.reduce(
    (sum, item) => (item.removed ? sum : sum + item.price * item.quantity),
    0
  );
  const rewardDiscount = order.appliedReward ? order.appliedReward.discountValue : 0;
  const method = order.discountMethod ?? 'manual';
  const manualDiscount = method === 'manual' ? order.manualDiscount ?? 0 : 0;
  const firstTableDiscount = method === 'first-table' ? getFirstTableDiscount(order) : 0;
  const couponDiscount = order.couponDiscountAmount ?? 0;
  const residentDiscount = order.residentDiscountAmount ?? 0;
  const itemsNet =
    totalBeforeDiscount - rewardDiscount - manualDiscount - firstTableDiscount - couponDiscount - residentDiscount;
  const deliveryCharge =
    order.orderType === 'delivery' ? Math.max(0, order.deliveryCharge ?? 0) : 0;
  return Math.round((itemsNet + deliveryCharge) * 100) / 100;
}

/**
 * NZ retail: menu line prices are GST-**inclusive** (same as POS receipts).
 * Split into ex-GST subtotal and GST portion using the 15/115 factor.
 */
export function totalsFromGstInclusiveItemSum(itemsTotalInclGst: number): {
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
} {
  const totalAmount = Math.round(itemsTotalInclGst * 100) / 100;
  const taxAmount = Math.round(totalAmount * (15 / 115) * 100) / 100;
  const subtotal = Math.round((totalAmount - taxAmount) * 100) / 100;
  return { subtotal, taxAmount, totalAmount };
}

/** Cart / quote lines with GST-inclusive unit prices (no order-level discounts). */
export function totalsFromGstInclusiveLineItems(
  lines: ReadonlyArray<{ price: number; quantity: number }>
): { subtotal: number; taxAmount: number; totalAmount: number } {
  const sum = lines.reduce((s, l) => s + (Number(l.price) || 0) * (Number(l.quantity) || 0), 0);
  return totalsFromGstInclusiveItemSum(sum);
}

/**
 * Compute subtotal, taxAmount, totalAmount for an order.
 * For Uber/DoorDash, uses stored totals when present (they include platform %).
 * For all other order types, always computes from current items (excl. removed) so display
 * and receipts stay correct after item add/remove, even if stored totals were stale.
 */
export function getOrderTotals(order: Order): { subtotal: number; taxAmount: number; totalAmount: number } {
  const useStored =
    order.orderType === 'uber-doordash' &&
    order.totalAmount != null &&
    order.subtotal != null &&
    order.taxAmount != null &&
    !Number.isNaN(order.totalAmount) &&
    order.totalAmount >= 0;
  if (useStored) {
    return {
      subtotal: order.subtotal,
      taxAmount: order.taxAmount,
      totalAmount: order.totalAmount,
    };
  }
  const totalAmount = getOrderTotalFromItems(order);
  const taxAmount = Math.round(totalAmount * (15 / 115) * 100) / 100;
  const subtotal = Math.round((totalAmount - taxAmount) * 100) / 100;
  return { subtotal, taxAmount, totalAmount };
}

/** Latest payment time, else order creation time. Matches Detailed Reports → Trends & Forecast day bucketing. */
export function getOrderSaleTimestampForReporting(order: Order): number {
  if (order.payments?.length) {
    return Math.max(...order.payments.map((p) => p.timestamp));
  }
  return order.timestamp;
}

/** YYYY-MM-DD in the given IANA timezone (e.g. Pacific/Auckland). */
export function getOrderSaleDateKeyInTimeZone(order: Order, timeZone: string): string {
  const ts = getOrderSaleTimestampForReporting(order);
  return new Date(ts).toLocaleDateString('en-CA', { timeZone });
}

/** Sum of completed payments (exclude pending). */
export function getAmountPaid(order: Order): number {
  return Math.round(
    (order.payments ?? [])
      .filter((p) => p.paymentStatus !== 'pending')
      .reduce((sum, p) => sum + p.amount, 0) * 100
  ) / 100;
}

/**
 * True if order has a balance due. Returns true when EITHER:
 *   - dollar balance (computed total - payments) is > 0.01, OR
 *   - any non-removed line still has unpaid quantity (catches floating-point drift on itemized
 *     sub-bill payments where dollar sum reconciles but a line shows 0/3 paid).
 */
export function hasBalanceDue(order: Order): boolean {
  const { totalAmount } = getOrderTotals(order);
  const paid = getAmountPaid(order);
  if (totalAmount - paid > 0.01) return true;
  if (hasUnpaidItems(order)) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Sub-bill / itemized payment helpers
// ---------------------------------------------------------------------------

/** Stable lineId helper: returns item.lineId, falling back to a synthetic one based on array index. */
export function getItemLineId(item: OrderItem, index: number): string {
  return item.lineId ?? `idx-${index}`;
}

/** True for combo-discount/negative-price helper lines that should not be selectable on their own. */
export function isOrderItemDiscountLine(item: OrderItem): boolean {
  if (typeof item.price === 'number' && item.price < 0) return true;
  if (typeof item.id === 'string' && (item.id as string).includes('combo-discount')) return true;
  return false;
}

/** Quantity of a line that has already been settled by an itemized sub-bill payment. */
export function getPaidQuantityForItem(item: OrderItem): number {
  const direct = item.paidQuantity ?? 0;
  if (direct > 0) return direct;
  // Defensive fallback: derive from per-payment map if direct counter wasn't written.
  const map = item.paidQuantityByPaymentId;
  if (map && typeof map === 'object') {
    let sum = 0;
    for (const k of Object.keys(map)) {
      const v = map[k];
      if (typeof v === 'number' && v > 0) sum += v;
    }
    return sum;
  }
  return 0;
}

/** Quantity of a line still available to be paid (clamped to 0). */
export function getUnpaidQuantityForItem(item: OrderItem): number {
  if (item.removed) return 0;
  const total = item.quantity ?? 0;
  const paid = getPaidQuantityForItem(item);
  return Math.max(0, total - paid);
}

/**
 * True if this order is using itemized (sub-bill) payment tracking.
 *
 * Detected by ANY of:
 *   - At least one item has `paidQuantity > 0` or a non-empty `paidQuantityByPaymentId` map.
 *   - Order has one or more saved sub-bills.
 *   - Any prior payment carried `itemAllocations`.
 *
 * Important for `hasUnpaidItems` / `hasBalanceDue`: if an order has NEVER used itemized payments,
 * the per-line `paidQuantity` counter will always be 0 even after a full dollar payment, so we
 * MUST NOT use line-level paid quantity to decide whether the order is settled — the dollar
 * balance is the only source of truth for those orders.
 */
export function isOrderItemized(order: Order): boolean {
  if (Array.isArray(order.subBills) && order.subBills.length > 0) return true;
  if (Array.isArray(order.payments) && order.payments.some((p) => Array.isArray(p.itemAllocations) && p.itemAllocations.length > 0)) return true;
  const items = order.items ?? [];
  for (const item of items) {
    if ((item.paidQuantity ?? 0) > 0) return true;
    const map = item.paidQuantityByPaymentId;
    if (map && typeof map === 'object' && Object.keys(map).length > 0) return true;
  }
  return false;
}

/**
 * True when at least one non-removed, non-discount line still has unpaid quantity.
 *
 * Returns `false` for orders that have NEVER been touched by itemized payments — for those orders,
 * line-level paid quantities are not maintained and the dollar balance is authoritative. This
 * prevents regular full-amount card/cash payments from being incorrectly flagged as "still has
 * unpaid items" just because `paidQuantity` defaulted to 0.
 */
export function hasUnpaidItems(order: Order): boolean {
  if (!isOrderItemized(order)) return false;
  const items = order.items ?? [];
  for (const item of items) {
    if (item.removed) continue;
    if (isOrderItemDiscountLine(item)) continue;
    if (getUnpaidQuantityForItem(item) > 0) return true;
  }
  return false;
}

/** Selection request from the SubBillBuilder: which lineIds and how many of each to pay for. */
export interface SubBillSelection {
  lineId: string;
  quantity: number;
}

export type SubBillDiscountAllocationMode = 'proportional' | 'remainder';

export interface SubBillTotals {
  /** Per-line allocations with money breakdown; ready to attach to a Payment.itemAllocations. */
  allocations: PaymentItemAllocation[];
  /** Sum of selected items' face value (price * qty, GST inclusive). */
  itemsSubtotal: number;
  /** Combined discount applied to this sub-bill (0 in remainder mode). */
  allocatedDiscount: number;
  /** Combined delivery share applied to this sub-bill (0 in remainder mode). */
  allocatedDelivery: number;
  /** GST portion of the sub-bill total (15/115 method). */
  gst: number;
  /** Net subtotal (excl. GST). */
  netSubtotal: number;
  /** Final money owed for this sub-bill (sum of allocations.allocatedTotal, rounded). */
  total: number;
  /** True if any selection exceeded the unpaid quantity for that line — the caller should refuse. */
  hasOverflow: boolean;
}

/**
 * Compute money breakdown for a sub-bill selection. Single source of truth used by both the
 * SubBillBuilder UI (preview) and the server-side validator in handleProcessPayment.
 *
 * Discount allocation modes:
 *  - 'proportional': each selected line gets a share of the order's discount/delivery proportional
 *    to its share of the order subtotal-before-discount. Fair across customers.
 *  - 'remainder': sub-bill pays face value (incl. GST); discount/delivery stay on the remaining balance.
 */
export function computeSubBillTotals(
  order: Order,
  selections: SubBillSelection[],
  mode: SubBillDiscountAllocationMode = 'proportional'
): SubBillTotals {
  const items = order.items ?? [];
  const itemByLineId = new Map<string, { item: OrderItem; index: number }>();
  items.forEach((it, idx) => {
    itemByLineId.set(getItemLineId(it, idx), { item: it, index: idx });
  });

  // Total order discount (rewards, manual, first-table, coupon, resident)
  const rewardDiscount = order.appliedReward ? order.appliedReward.discountValue : 0;
  const method = order.discountMethod ?? 'manual';
  const manualDiscount = method === 'manual' ? order.manualDiscount ?? 0 : 0;
  const firstTableDiscount = method === 'first-table' ? getFirstTableDiscount(order) : 0;
  const couponDiscount = order.couponDiscountAmount ?? 0;
  const residentDiscount = order.residentDiscountAmount ?? 0;
  const totalOrderDiscount =
    rewardDiscount + manualDiscount + firstTableDiscount + couponDiscount + residentDiscount;

  // Order delivery (manual delivery only)
  const totalOrderDelivery =
    order.orderType === 'delivery' ? Math.max(0, order.deliveryCharge ?? 0) : 0;

  // Order's total face value (sum of price * quantity for active, non-discount lines).
  // Used only to compute proportional shares so each customer's allocated discount sums to the whole.
  const orderItemsFaceValue = items.reduce((sum, it) => {
    if (it.removed) return sum;
    if (isOrderItemDiscountLine(it)) return sum;
    return sum + it.price * it.quantity;
  }, 0);

  let hasOverflow = false;
  const allocations: PaymentItemAllocation[] = [];

  for (const sel of selections) {
    const found = itemByLineId.get(sel.lineId);
    if (!found) continue;
    const { item } = found;
    if (item.removed) continue;
    if (isOrderItemDiscountLine(item)) continue;
    const qty = Math.max(0, Math.floor(sel.quantity || 0));
    if (qty <= 0) continue;
    if (qty > getUnpaidQuantityForItem(item)) {
      hasOverflow = true;
      continue;
    }

    const lineSubtotal = round2(item.price * qty);

    let allocatedDiscount = 0;
    let allocatedDelivery = 0;
    if (mode === 'proportional' && orderItemsFaceValue > 0) {
      const share = (item.price * qty) / orderItemsFaceValue;
      allocatedDiscount = round2(totalOrderDiscount * share);
      allocatedDelivery = round2(totalOrderDelivery * share);
    }

    const allocatedTotalRaw = lineSubtotal - allocatedDiscount + allocatedDelivery;
    const allocatedTotal = round2(allocatedTotalRaw);
    const allocatedTax = round2(allocatedTotal * (15 / 115));

    allocations.push({
      lineId: sel.lineId,
      quantity: qty,
      lineSubtotal,
      allocatedDiscount,
      allocatedDelivery,
      allocatedTax,
      allocatedTotal,
    });
  }

  const itemsSubtotal = round2(allocations.reduce((s, a) => s + a.lineSubtotal, 0));
  const allocatedDiscount = round2(allocations.reduce((s, a) => s + a.allocatedDiscount, 0));
  const allocatedDelivery = round2(allocations.reduce((s, a) => s + a.allocatedDelivery, 0));
  const total = round2(allocations.reduce((s, a) => s + a.allocatedTotal, 0));
  const gst = round2(total * (15 / 115));
  const netSubtotal = round2(total - gst);

  return {
    allocations,
    itemsSubtotal,
    allocatedDiscount,
    allocatedDelivery,
    gst,
    netSubtotal,
    total,
    hasOverflow,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * True if order has at least one real line item (not removed, not discount-only, quantity > 0).
 * Used for: floor plan occupancy, table status sync, Pending list — so empty/ghost orders don't mark tables or show in Pending.
 */
export function orderHasMeaningfulItems(order: Order): boolean {
  const items = order.items ?? [];
  if (!Array.isArray(items)) return false;
  return items.some((item) => {
    if (item.removed) return false;
    if ((item.quantity ?? 0) <= 0) return false;
    if (typeof item.price === 'number' && item.price < 0) return false;
    if (typeof item.id === 'string' && item.id.includes('combo-discount')) return false;
    return true;
  });
}

const FLOOR_PLAN_DINE_IN_STATUSES: OrderStatus[] = [
  'new',
  'preparing',
  'ready',
  'served',
  'paused',
  'held',
  'queued',
];

/** True when an order is tied to a floor-plan table (numeric id or legacy tableNumber). */
export function orderMatchesFloorPlanTable(order: Order, tableId: number): boolean {
  const tableIdNum = Number(tableId);
  if (!Number.isFinite(tableIdNum)) return false;
  return (
    Number(order.tableId) === tableIdNum ||
    (order.tableNumber != null && Number(order.tableNumber) === tableIdNum)
  );
}

function isOrderOnBusinessDay(order: Order, businessTimeZone: string): boolean {
  const placedMs = orderCreatedAtMs(order);
  if (!Number.isFinite(placedMs) || placedMs <= 0) return false;
  const orderDayKey = new Date(placedMs).toLocaleDateString('en-CA', { timeZone: businessTimeZone });
  const todayKey = ymdInTimeZone(businessTimeZone);
  return orderDayKey === todayKey;
}

/**
 * True when an order should mark a table occupied on the floor plan.
 * Open unpaid dine-in / buffet tabs stay occupied until paid (may cross midnight).
 */
export function isFloorPlanActiveOrder(
  order: Order,
  options?: { businessTimeZone?: string; nowMs?: number },
): boolean {
  if (order.isDeleted) return false;
  if (order.status === 'cancelled' || order.status === 'refunded' || order.status === 'comped') return false;
  if (!orderHasMeaningfulItems(order)) return false;

  const nowMs = options?.nowMs ?? Date.now();

  if (order.orderType === 'buffet') {
    if (order.paymentStatus === 'paid') return false;
    return order.status === 'buffet' || order.status === 'new';
  }

  const isDineIn =
    order.orderType === 'dine-in' ||
    (order.tableId != null &&
      order.orderType !== 'buffet' &&
      order.orderType !== 'takeaway' &&
      order.orderType !== 'online' &&
      order.orderType !== 'delivery' &&
      order.orderType !== 'uber-doordash' &&
      order.orderType !== 'event');

  if (!isDineIn) return false;
  if (order.paymentStatus === 'paid') return false;

  if (order.status === 'served') {
    const servedMs = toMillis(order.servedAt ?? order.timestamp);
    return servedMs > nowMs - 24 * 60 * 60 * 1000;
  }

  return FLOOR_PLAN_DINE_IN_STATUSES.includes(order.status);
}

/** Floor-plan occupancy for a specific table (visual + table status sync). */
export function isFloorPlanActiveTableOrder(
  order: Order,
  tableId: number,
  businessTimeZone?: string,
): boolean {
  if (!orderMatchesFloorPlanTable(order, tableId)) return false;
  return isFloorPlanActiveOrder(order, { businessTimeZone });
}

function orderTimestampMs(order: Order): number {
  const ts = order.timestamp;
  if (typeof ts === 'number') return ts;
  if (ts instanceof Date) return ts.getTime();
  if (ts && typeof (ts as { seconds?: number }).seconds === 'number') {
    return (ts as { seconds: number }).seconds * 1000;
  }
  return 0;
}

function isTodayInNz(timestamp: number): boolean {
  const timeZone = 'Pacific/Auckland';
  const orderDateStr = new Date(timestamp).toLocaleDateString('en-CA', { timeZone });
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone });
  return orderDateStr === todayStr;
}

/** NZ calendar day for placed time or (online) scheduled pickup — matches POS Pending. */
export function isKitchenRelevantCalendarDay(order: Order): boolean {
  const placedMs = orderCreatedAtMs(order);
  if (placedMs > 0 && isTodayInNz(placedMs)) return true;
  if (order.orderType === 'online') {
    const pickupMs = toMillis(order.pickupTime);
    if (pickupMs > 0 && isTodayInNz(pickupMs)) return true;
  }
  return false;
}

/** True when an order should appear on kitchen boards (not terminal / served). */
export function isOpenKitchenTicketStatus(order: Order): boolean {
  if (
    order.status === 'cancelled' ||
    order.status === 'refunded' ||
    order.status === 'comped' ||
    order.status === 'served'
  ) {
    return false;
  }
  return order.status === 'new' || order.status === 'preparing' || order.status === 'ready';
}

/**
 * Paid online tickets whose pickup window passed long ago with no kitchen progress.
 * Hides abandoned test / never-prepped orders from KDS without affecting POS Pending.
 */
export function isStaleOnlineKitchenTicket(order: Order, nowMs: number = Date.now()): boolean {
  if (order.orderType !== 'online' || order.paymentStatus !== 'paid') return false;
  if (order.status !== 'new' && order.status !== 'preparing') return false;
  const pickupMs = toMillis(order.pickupTime);
  if (pickupMs <= 0) return false;
  const hasKitchenProgress =
    order.status === 'preparing' ||
    order.preparingStartedAt != null ||
    (order.items ?? []).some((item) => (item.quantityCompleted ?? 0) > 0);
  if (hasKitchenProgress) return false;
  const staleGraceMs = 2 * 60 * 60 * 1000;
  return nowMs > pickupMs + staleGraceMs;
}

/** Pickup / platform orders that leave Pending once staff confirms handoff. */
function isHandedOffPickupOrder(order: Order): boolean {
  return (
    order.status === 'served' &&
    (order.orderType === 'uber-doordash' ||
      order.orderType === 'takeaway' ||
      order.orderType === 'online' ||
      order.orderType === 'delivery' ||
      order.orderType === 'event')
  );
}

/**
 * True when an order belongs in the POS Pending queue.
 * Uber/DoorDash orders stay unpaid in POS (paid via platform) but must drop off Pending once served.
 */
export function isPendingPosOrder(order: Order): boolean {
  if (order.status === 'cancelled' || order.status === 'refunded' || order.status === 'comped') return false;
  if (order.isDeleted) return false;
  if (!orderHasMeaningfulItems(order)) return false;
  if (isHandedOffPickupOrder(order)) return false;

  if (order.paymentStatus === 'unpaid') {
    if (isUnpaidOnlineWebOrder(order)) return false;
    return true;
  }

  const orderTs = orderTimestampMs(order);

  if (order.orderType === 'takeaway' && order.status !== 'served') {
    return isTodayInNz(orderTs);
  }

  if (order.orderType === 'online' && order.paymentStatus === 'paid' && order.status !== 'served') {
    return isTodayInNz(orderTs);
  }

  if (order.orderType === 'buffet' && order.status === 'buffet') return true;

  return false;
}

/** True when the POS sidebar should treat the order as fully done (no Pay / pickup actions). */
export function isPosOrderFullySettled(order: Order): boolean {
  if (isPendingPosOrder(order)) return false;
  if (hasBalanceDue(order)) return false;
  if (order.orderType === 'uber-doordash') return order.status === 'served';
  return order.paymentStatus === 'paid';
}

/**
 * True when an order belongs in the POS Completed list.
 * Uber/DoorDash orders complete on handoff even if POS paymentStatus is still unpaid.
 */
export function isCompletedPosOrder(order: Order): boolean {
  if (order.status === 'cancelled' || order.status === 'refunded' || order.isDeleted) return false;
  if (!orderHasMeaningfulItems(order)) return false;

  if (order.orderType === 'uber-doordash') return order.status === 'served';

  const hasPayment = Array.isArray(order.payments) && order.payments.some((p) => p.amount > 0);
  const isPaidLike = order.paymentStatus === 'paid' || hasPayment;
  if (!isPaidLike) return false;

  if (order.orderType === 'takeaway' && order.status !== 'served') return false;
  if (order.orderType === 'online' && order.status !== 'served') return false;
  if (order.orderType === 'buffet' && order.status !== 'served') return false;
  if (order.orderType === 'event' && order.status !== 'served') return false;

  return true;
}

/**
 * Number of seats/covers used by this order for table capacity.
 * Buffet: sum of item quantities (e.g. "Lunch Buffet x 4" => 4). Otherwise 1 per order.
 */
export function getSeatsUsedByOrder(order: Order): number {
  if (order.orderType === 'buffet' && order.items?.length) {
    const total = order.items
      .filter((item) => !item.removed && (item.quantity ?? 0) > 0)
      .reduce((sum, item) => sum + (item.quantity ?? 0), 0);
    return total > 0 ? total : 1;
  }
  return 1;
}

/**
 * Seats used at a table by the given orders (e.g. active table orders).
 * Used with table.capacity to get spareSeats = capacity - seatsUsed.
 */
export function getTableSeatsUsed(orders: Order[]): number {
  return orders.reduce((sum, o) => sum + getSeatsUsedByOrder(o), 0);
}

export { isUnpaidOnlineWebOrder } from './onlineOrderVisibility';
